// Pre-screener wiring.
//
// Every Prolific tester lands on /screener before they're allowed into the
// rest of the app. The criteria match what Yarit told me on 2026-06-12 —
// must be an *active*, *family* caregiver, providing care at least weekly,
// age 25+ — with one important update on 2026-06-29: location is no
// longer US-only. Memory Lane now ships country/language packs for every
// country in SUPPORTED_COUNTRIES (US, CA, MX, GB, IE, AU, NZ, DE, FR, ES),
// so the screener accepts caregivers from any of them.

import { isProlificTester } from "@/lib/prolific";
import { SUPPORTED_COUNTRIES } from "@/lib/countries";

export const SCREEN_OUT_PROLIFIC_CODE = "C1HDHBIO";
export const SCREEN_OUT_PROLIFIC_URL = `https://app.prolific.com/submissions/complete?cc=${SCREEN_OUT_PROLIFIC_CODE}`;

// Country options shown on the screener. Sourced directly from
// SUPPORTED_COUNTRIES (so this list stays in sync the day we add an
// 11th country) + a single "Somewhere else" screen-out option.
//
// Values use the lowercased ISO-3166 alpha-2 code so analytics, GA4
// events, and the saved screener payload all line up with what the
// rest of the app already stores in user.profile.country.
const COUNTRY_OPTIONS = [
    ...SUPPORTED_COUNTRIES.map((c) => ({
        value: c.code.toLowerCase(),
        label: `${c.flag} ${c.label}`,
    })),
    { value: "outside_supported", label: "Somewhere else" },
];

const SUPPORTED_COUNTRY_VALUES = SUPPORTED_COUNTRIES.map((c) => c.code.toLowerCase());

export const SCREENER_QUESTIONS = [
    {
        id: "cared_for_dementia",
        text: "Are you currently caring for, or have you cared for, someone with dementia or Alzheimer's?",
        sub: "Either now or in the past — both count.",
        options: [
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" },
        ],
        pass: (v) => v === "yes",
    },
    {
        id: "relationship",
        text: "What's your relationship to the person you care for?",
        sub: "Memory Lane is built for family caregivers right now.",
        options: [
            { value: "parent", label: "Parent" },
            { value: "spouse", label: "Spouse or partner" },
            { value: "sibling", label: "Sibling" },
            { value: "child", label: "My child" },
            { value: "grandparent", label: "Grandparent" },
            { value: "other_family", label: "Other family member" },
            { value: "friend", label: "Friend / neighbor" },
            { value: "paid", label: "Paid caregiver (CNA, home health, etc.)" },
            { value: "none", label: "I'm not caring for anyone right now" },
        ],
        pass: (v) => ["parent", "spouse", "sibling", "child", "grandparent", "other_family"].includes(v),
    },
    {
        id: "frequency",
        text: "How often are you providing care right now?",
        options: [
            { value: "daily", label: "Daily" },
            { value: "several_week", label: "Several times a week" },
            { value: "weekly", label: "About once a week" },
            { value: "monthly", label: "A few times a month" },
            { value: "less", label: "Less than monthly" },
        ],
        pass: (v) => ["daily", "several_week", "weekly"].includes(v),
    },
    {
        id: "country",
        text: "Where are you located?",
        sub: "Memory Lane currently ships country-specific guides for the countries below — pick whichever is closest to where you actually live and care.",
        options: COUNTRY_OPTIONS,
        pass: (v) => SUPPORTED_COUNTRY_VALUES.includes(v),
    },
    {
        id: "age",
        text: "What's your age?",
        options: [
            { value: "u25", label: "Under 25" },
            { value: "25_39", label: "25–39" },
            { value: "40_59", label: "40–59" },
            { value: "60p", label: "60+" },
        ],
        pass: (v) => ["25_39", "40_59", "60p"].includes(v),
    },
];

export function evaluateScreener(answers) {
    const failed = [];
    for (const q of SCREENER_QUESTIONS) {
        const v = answers?.[q.id];
        if (!v || !q.pass(v)) {
            failed.push(q.id);
        }
    }
    return { qualified: failed.length === 0, failed_on: failed };
}

export function screenOutRedirectUrl() {
    // Only Prolific gets a paid screen-out — for everyone else we show a
    // friendly thank-you page inside the app.
    return isProlificTester() ? SCREEN_OUT_PROLIFIC_URL : "";
}

export function needsScreener(user) {
    if (!user || user === false) return false;
    if (user.role === "admin") return false;
    if (!user.tester_code) return false;
    if (user.screener?.completed_at) return false;
    // Only Prolific testers are gated by the screener. One-off iid links
    // (admin-issued for Reddit/email/etc. outreach) skip it because the
    // founder has already vetted those testers manually.
    if (user.tester_source !== "prolific") return false;
    return true;
}

export function wasScreenedOut(user) {
    return !!(user && user !== false && user.screener?.completed_at && user.screener?.qualified === false);
}
